import { useState } from "react";
import GHG_Icon from "../../Assets/Icons/GHG_Icon.png";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";

import HolosStatusBadge from "../HolosStatusBadge";
import GHGEmissionsBreakdownChart from "../Charts/GHGEmissionsBreakdownChart";
import useHolosJob from "../../hooks/useHolosJob";
import { useAuth } from "../Auth/useAuth";
import { HOLOS_UNSUPPORTED_CROP_MESSAGE } from "../../constants/holos";

const NON_TERMINAL_STATUSES = ["PENDING", "SENDING", "RETRY"];

export default function IndicatorGHG({
  crop,
  year,
  value,
  ghgSource,
  cropyearId,
  cropYearVersionId,
  isSupportedCrop = false,
  isClosed = false,
}) {
  const [isCollapsed, setIsCollapsed] = useState(isClosed);
  const { user } = useAuth();
  const { job, error, recalculate } = useHolosJob(cropyearId, user);

  const jobIsCurrent =
    !job ||
    !cropYearVersionId ||
    String(job.crop_year_version_id) === String(cropYearVersionId);
  const isCalculating =
    jobIsCurrent && job && NON_TERMINAL_STATUSES.includes(job.status);
  const isCompleted = jobIsCurrent && job && job.status === "COMPLETED";
  const isTerminalFailure =
    jobIsCurrent &&
    job &&
    (job.status === "FAILED" || job.status === "REJECTED_UNSUPPORTED");
  const emissions = isCompleted ? job.response_payload?.emissions : null;

  async function handleRecalculate() {
    try {
      await recalculate();
    } catch {
      // surfaced via the hook's `error` state below
    }
  }

  const SavedHolosResult = () => (
    <div className="w-full flex flex-col items-center md:flex-row mt-4">
      <div className="w-[246px] mb-8 md:mb-0 min-w-[246px] max-h-[114px] border-[rgb(221,221,221)] border-[1px] flex flex-col items-center">
        <div className="text-center w-full bg-[rgb(0,164,229)]">
          <p className="py-[8px] text-[18px] text-white font-medium">
            Greenhouse Gas
          </p>
        </div>
        <div className="text-[16px]">{`${year} ${crop}`}</div>
        <div className="text-[24px] text-[rgb(0,164,229)] font-light">
          {value}
        </div>
        <div className="text-[16px]">
          kg CO2e / acre
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full px-4 mb-4 rounded border-[rgb(230,230,230)] border-[1px] shadow-md">
      {/* Header  */}
      <button
        aria-controls="greenhouse-gas-details"
        aria-expanded={!isCollapsed}
        aria-label="Toggle Greenhouse Gas details"
        className="h-[74px] w-full flex items-center justify-between cursor-pointer bg-transparent text-left"
        onClick={() => setIsCollapsed(!isCollapsed)}
        type="button"
      >
        <div className="flex items-center">
          <img className="h-[50px] w-[50px]" src={GHG_Icon} alt="" />
          <p className="text-[#666666] text-[18px] ml-[12px]">Greenhouse Gas</p>
          {job && jobIsCurrent && (
            <span className="ml-3">
              <HolosStatusBadge status={job.status} />
            </span>
          )}
          {job && !jobIsCurrent ? (
            <Chip className="ml-3" size="small" label="Prior version" />
          ) : null}
        </div>
        <i>
          {isCollapsed ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
        </i>
      </button>

      {/* BODY */}
      <div
        id="greenhouse-gas-details"
        className={`transition-all duration-500 overflow-hidden leading-6 ${
          isCollapsed ? "h-0 p-0" : "pb-8"
        }`}
      >
        <p>
          The Greenhouse Gas (GHG) Emissions metric models field-level
          emissions from fertilizer use, fuel/energy use, and soil carbon
          change. This result is modeled exclusively by Holos (Agriculture and
          Agri-Food Canada's GHG calculation engine) using your field's location,
          crop, tillage, and fertilizer data.
        </p>

        {!isSupportedCrop ? (
          <Alert severity="warning" className="mt-4">
            {HOLOS_UNSUPPORTED_CROP_MESSAGE}
          </Alert>
        ) : null}

        {error && (
          <Alert severity="error" className="mt-4">
            Couldn't load the Holos calculation status. Please try again.
          </Alert>
        )}

        {job && !jobIsCurrent && isSupportedCrop ? (
          <Alert severity="warning" className="mt-4">
            The latest Holos result belongs to an earlier crop-year version.
            <div className="mt-3">
              <Button variant="outlined" size="small" onClick={handleRecalculate}>
                Calculate current version
              </Button>
            </div>
          </Alert>
        ) : null}

        {!job && !error && isSupportedCrop && (
          <div className="mt-4">
            <Button variant="contained" onClick={handleRecalculate}>
              Calculate GHG with Holos
            </Button>
          </div>
        )}

        {isCalculating && (
          <div className="mt-4 flex items-center">
            <CircularProgress size={20} className="mr-3" />
            <p>
              Calculating your field's GHG emissions with Holos --- this can take
              up to a few minutes.
            </p>
          </div>
        )}

        {isCompleted && emissions && (
          <div className="mt-4">
            <div className="text-[16px]">{`${year} ${crop}`}</div>
            <div className="text-[28px] text-[rgb(0,164,229)] font-light">
              {Number(emissions.kgCo2ePerAcre).toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })}{" "}
              <span className="text-[16px]">kg CO2e / acre</span>
            </div>
            <p className="text-[13px] text-[#666666] mb-4">
              Negative values indicate net carbon sequestration.
            </p>

            {job.data_quality_flags && job.data_quality_flags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {job.data_quality_flags.map((flag) => (
                  <Chip
                    key={flag}
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={flag.replaceAll("_", " ")}
                  />
                ))}
              </div>
            )}

            <GHGEmissionsBreakdownChart emissions={emissions} />

            {job.warnings && job.warnings.length > 0 && (
              <div className="mt-4">
                {job.warnings.map((warning) => (
                  <Alert
                    key={warning.code}
                    severity="info"
                    className="mb-2"
                  >
                    {warning.message}
                  </Alert>
                ))}
              </div>
            )}

            <div className="mt-4">
              <Button variant="outlined" size="small" onClick={handleRecalculate} disabled={!isSupportedCrop}>
                Recalculate
              </Button>
            </div>
          </div>
        )}

        {isTerminalFailure && (
          <div className="mt-4">
            <Alert
              severity={job.status === "REJECTED_UNSUPPORTED" ? "warning" : "error"}
            >
              {job.last_error ||
                "This crop year's Holos calculation could not be completed."}
            </Alert>
            <p className="text-[13px] text-[#666666] mt-2 mb-2">
              If this crop, province, or field location changed recently, review
              those fields and try again.
            </p>
            <Button variant="outlined" size="small" onClick={handleRecalculate} disabled={!isSupportedCrop}>
              Try Again
            </Button>
          </div>
        )}

        {!isCompleted && ghgSource === "holos" && value != null ? (
          <SavedHolosResult />
        ) : null}
      </div>
    </div>
  );
}
